import { useContext, useEffect } from 'react';
import { TabbedLayout } from '@backstage/core-components';
import ActivityPage from './components/ActivityPage';
import { Navigate, Route, Routes, useParams, useLocation } from 'react-router';
import { ITPContext, TPContext } from './providers/TPProvider';
import OverviewPage from './components/OverviewPage';
import EditDetailsPage from './components/EditDetailsPage';
import { useGetApmInfoById } from './hooks/useGetApmInfoById';
import { AuthContext } from '../../providers/AuthProvider';
import AdminsPage from './components/AdminsPage';
import { ServiceRoutes } from './components/services/ServiceRoutes';
import ProjectMapping from './components/ProjectMapping';
import TPErrorBoundary from './components/common/TPErrorBoundary';
import QEScorePage from './components/qescore';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  useDevxGroupBaseEnabled,
  useIQEAgentEnabled,
  useQEScoreAgentEnabled,
} from '../../hooks/useFeatureFlag';
import IQeRoutes from './components/IQEAgent/IQeRoutes';

const TPTabs = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const { allApms, setApmInfo, apmInfo, myApms }: ITPContext =
    useContext(TPContext);

  const [apmDetails] = useGetApmInfoById(projectId, allApms);

  useEffect(() => {
    if (apmDetails) setApmInfo(apmDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apmDetails]);

  const { profEmail, isAdmin } = useContext(AuthContext);
  const isToolAdmin =
    isAdmin ||
    profEmail?.toLowerCase() ===
      apmInfo?.PrimaryBusinessOwnerEmail?.toLowerCase() ||
    profEmail?.toLowerCase() ===
      apmInfo?.PrimarySupportOwnerEmail?.toLowerCase() ||
    apmInfo?.admins?.includes(profEmail) ||
    myApms?.includes(projectId!)
      ? true
      : false;

  const isInHouseInvestApp =
    apmInfo?.Recommendation === 'Invest' &&
    apmInfo?.SoftwareType === 'In house Developed';

  const { isReady: isFeatureFlagsReady } = useFeatureFlags();
  const groupBaseEnabled = useDevxGroupBaseEnabled();
  const isIQEFeatureEnabled = useIQEAgentEnabled();
  const isQEScoreFeatureEnabled = useQEScoreAgentEnabled();

  const showIQETab = groupBaseEnabled && isIQEFeatureEnabled;
  const showQEScoreTab =
    isInHouseInvestApp && groupBaseEnabled && isQEScoreFeatureEnabled;

  // Check if user came from QE Score navigation
  const getDefaultRoute = () => {
    // Check if we came from qe-score route and app is eligible
    if (location.pathname.includes('/qe-score') && showQEScoreTab) {
      return './qe-score';
    } else if (location.pathname.includes('/iqe-agent') && showIQETab) {
      return './iqe-agent';
    }
    return './services';
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
        <Route
          path="/qe-score"
          element={
            apmInfo &&
            apmInfo.ApmNumber === projectId &&
            isFeatureFlagsReady &&
            !showQEScoreTab ? (
              <Navigate to="../services" replace />
            ) : null
          }
        />
        <Route
          path="/iqe-agent"
          element={
            apmInfo &&
            apmInfo.ApmNumber === projectId &&
            isFeatureFlagsReady &&
            !showIQETab ? (
              <Navigate to="../services" replace />
            ) : null
          }
        />
      </Routes>

      {apmInfo !== null ? (
        <TabbedLayout>
          <TabbedLayout.Route path="/services" title="Services">
            <ServiceRoutes isToolAdmin={isToolAdmin} />
          </TabbedLayout.Route>
          <TabbedLayout.Route path="/apm-info" title="Apm Info">
            <Routes>
              <Route path="/" element={<OverviewPage />} />
              <Route path="/edit-details" element={<EditDetailsPage />} />
            </Routes>
          </TabbedLayout.Route>
          <TabbedLayout.Route path="/project-mapping" title="Project Mapping">
            <TPErrorBoundary title="Project Mapping failed to load">
              <ProjectMapping />
            </TPErrorBoundary>
          </TabbedLayout.Route>

          {/* <TabbedLayout.Route path="/tools" title="Tools">
                <ToolsManagementPage type="tool" key="tool" />
              </TabbedLayout.Route> */}
          {/* <TabbedLayout.Route
          path="/users-&-user-groups"
          title="Users & User Groups"
        >
          <UserGroupsPage />
        </TabbedLayout.Route> */}
          <TabbedLayout.Route path="/activity" title="Activity">
            <ActivityPage />
          </TabbedLayout.Route>
          <TabbedLayout.Route path="/admins" title="Admins">
            <AdminsPage isToolAdmin={isToolAdmin} />
          </TabbedLayout.Route>
          {showQEScoreTab && (
            <TabbedLayout.Route path="/qe-score" title="QE Score">
              <QEScorePage
                isToolAdmin={isToolAdmin}
                applicationName={apmInfo?.Name}
              />
            </TabbedLayout.Route>
          )}

          {showIQETab && (
            <TabbedLayout.Route path="/iqe-agent" title="IQE Agent">
              <IQeRoutes isToolAdmin={isToolAdmin} />
            </TabbedLayout.Route>
          )}
        </TabbedLayout>
      ) : null}
    </>
  );
};
export default TPTabs;
